import { useEffect, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  buildOpinionPayload,
  emptyOpinionForm,
  OPINION_TITLE_MAX,
  opinionToForm,
  validateOpinionForm,
} from './dailyTasksUtils';
import { uploadPulseImage, getPulseUploadedImageUrl, isPulseApiSuccess, getPulseErrorMessage } from '../../services/pulseApi';

const HERO_PRESENTATIONS = [
  { value: '', label: 'Default (bleed)' },
  { value: 'bleed', label: 'Bleed — photo band, content below' },
  { value: 'backdrop', label: 'Backdrop — photo behind question' },
];

/**
 * Daily Tasks Opinion editor — Pulse hot-take *shape* (question + 2/4 options),
 * saved as activity_type=opinion. Does not create Pulse feed cards.
 */
function OpinionFormModal({ open, onClose, onSubmit, opinion, saving }) {
  const [form, setForm] = useState(() => emptyOpinionForm());
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (opinion) setForm(opinionToForm(opinion));
    else setForm(emptyOpinionForm());
    setErrors([]);
  }, [open, opinion]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const updateOption = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      options: prev.options.map((o, i) => (i === index ? { ...o, [field]: value } : o)),
    }));
  };

  const addTwoOptions = () => {
    setForm((prev) => {
      if (prev.options.length >= 4) return prev;
      const n = prev.options.length;
      return {
        ...prev,
        options: [
          ...prev.options,
          { id: `opt_${n + 1}`, label: '' },
          { id: `opt_${n + 2}`, label: '' },
        ].slice(0, 4),
      };
    });
  };

  const removeOption = (index) => {
    setForm((prev) => {
      if (prev.options.length <= 2) return prev;
      return { ...prev, options: prev.options.filter((_, i) => i !== index) };
    });
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setErrors([]);
    try {
      const result = await uploadPulseImage(file);
      if (!isPulseApiSuccess(result)) {
        setErrors([getPulseErrorMessage(result, 'Failed to upload image.')]);
        return;
      }
      const url = getPulseUploadedImageUrl(result);
      if (!url) {
        setErrors(['Upload finished but image URL was missing.']);
        return;
      }
      update({ image_url: url });
    } catch (err) {
      setErrors([err.message || 'Failed to upload image.']);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = () => {
    const errs = validateOpinionForm(form);
    setErrors(errs);
    if (errs.length) return;
    onSubmit(buildOpinionPayload(form));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{opinion ? 'Edit Opinion' : 'Add Opinion'}</DialogTitle>
      <DialogContent>
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {errors.join(' ')}
          </Alert>
        )}
        <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
          Pulse-like authoring (question, image, 2 or 4 options, no correct answer) with{' '}
          <code>activity_type=opinion</code> and <code>config.surface=daily_page</code>. Does{' '}
          <strong>not</strong> insert Pulse cards / feed rows. Draft → hidden from{' '}
          <code>GET /dailies/today</code>.
        </Alert>

        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              required
              label="Title (the question)"
              value={form.title}
              onChange={(e) => update({ title: e.target.value.slice(0, OPINION_TITLE_MAX) })}
              helperText={`${form.title.length}/${OPINION_TITLE_MAX} — clipped to 70. No correct answer.`}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Status"
              value={form.status}
              onChange={(e) => update({ status: e.target.value })}
            >
              <MenuItem value="active">Active (publish)</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Summary (optional)"
              value={form.summary}
              onChange={(e) => update({ summary: e.target.value })}
              multiline
              minRows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Image URL (optional)"
              value={form.image_url}
              onChange={(e) => update({ image_url: e.target.value })}
              helperText="HTTPS CDN URL, or upload. Empty → no photo."
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                component="label"
                startIcon={<CloudUploadIcon />}
                disabled={uploading || saving}
              >
                {uploading ? 'Uploading…' : 'Upload'}
                <input
                  ref={fileInputRef}
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={handleUploadImage}
                />
              </Button>
            </Box>
            {form.image_url ? (
              <Box
                component="img"
                src={form.image_url}
                alt=""
                sx={{ mt: 1, maxHeight: 120, maxWidth: 240, objectFit: 'cover', borderRadius: 1 }}
              />
            ) : null}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Hero presentation"
              value={form.hero_presentation}
              onChange={(e) => update({ hero_presentation: e.target.value })}
              helperText="Omit → app bleed"
            >
              {HERO_PRESENTATIONS.map((item) => (
                <MenuItem key={item.value || 'default'} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Hero aspect ratio"
              value={form.hero_aspect_ratio}
              onChange={(e) => update({ hero_aspect_ratio: e.target.value })}
              placeholder="4:3"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Reward FE"
              value={form.reward_fe}
              onChange={(e) => update({ reward_fe: e.target.value })}
              helperText="Default 5"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Entry fee FE"
              value={form.entry_fee_fe}
              onChange={(e) => update({ entry_fee_fe: e.target.value })}
              helperText="Default free"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Starts at"
              value={form.starts_at}
              onChange={(e) => update({ starts_at: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Empty → now"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Deadline at"
              value={form.deadline_at}
              onChange={(e) => update({ deadline_at: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Expires at"
              value={form.expires_at}
              onChange={(e) => update({ expires_at: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 1 }}>
          <Typography variant="subtitle2">Options (2 or 4)</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={form.options.length >= 4}
            onClick={addTwoOptions}
          >
            {form.options.length === 2 ? 'Add two options' : 'Add option'}
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Leave Agree / Disagree, or write 2 or 4 labels (2–9 characters). No correct answer.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Label</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {form.options.map((option, index) => (
              <TableRow key={index}>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={option.id}
                    onChange={(e) => updateOption(index, 'id', e.target.value)}
                    placeholder="yes"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={option.label}
                    onChange={(e) => updateOption(index, 'label', e.target.value.slice(0, 9))}
                    helperText={`${String(option.label || '').length}/9`}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => removeOption(index)}
                    disabled={form.options.length <= 2}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || uploading}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default OpinionFormModal;
